import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Users, UserCog, Briefcase, Clock, HardHat, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Dialog to batch-create work_entries on an order from a date range
 * for multiple employees and/or subcontractors.
 */
export const WorkSessionsDialog = ({ open, onClose, order }) => {
  const [employees, setEmployees] = useState([]);
  const [subcontractors, setSubcontractors] = useState([]);
  const [existing, setExisting] = useState({ employees: [], subcontractors: [] });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    start_date: "",
    end_date: "",
    employee_ids: [],
    subcontractor_ids: [],
    slot: "both",
    include_weekends: false,
  });

  useEffect(() => {
    if (!open || !order) return;
    setForm({ start_date: "", end_date: "", employee_ids: [], subcontractor_ids: [], slot: "both", include_weekends: false });
    setLoading(true);
    Promise.all([
      api.getEmployees(),
      api.getSubcontractors(),
      api.getOrderWorkers(order.id),
    ])
      .then(([empRes, subRes, existRes]) => {
        setEmployees((empRes.data || []).filter(e => !e.exit_date && !e.archived));
        setSubcontractors(subRes.data || []);
        // Backend renvoie {workers: [{kind, id, name, total_hours, total_days, dates, ...}]}
        // On split en deux listes pour l'UI legacy de ce dialog.
        const wks = existRes.data?.workers || [];
        setExisting({
          employees: wks
            .filter((w) => w.kind === "employee")
            .map((w) => ({ employee_id: w.id, name: w.name, days_worked: w.total_days, total_hours: w.total_hours })),
          subcontractors: wks
            .filter((w) => w.kind === "subcontractor")
            .map((w) => ({ subcontractor_id: w.id, name: w.name, days_worked: w.total_days, total_hours: w.total_hours })),
        });
      })
      .catch(() => toast.error("Erreur chargement"))
      .finally(() => setLoading(false));
  }, [open, order]);

  const toggleEmployee = (id) => {
    setForm((f) => ({
      ...f,
      employee_ids: f.employee_ids.includes(id)
        ? f.employee_ids.filter((x) => x !== id)
        : [...f.employee_ids, id],
    }));
  };
  const toggleSubcontractor = (id) => {
    setForm((f) => ({
      ...f,
      subcontractor_ids: f.subcontractor_ids.includes(id)
        ? f.subcontractor_ids.filter((x) => x !== id)
        : [...f.subcontractor_ids, id],
    }));
  };

  const handleSubmit = async () => {
    if (!form.start_date || !form.end_date) {
      toast.error("Sélectionnez les dates de début et de fin");
      return;
    }
    if (form.employee_ids.length === 0 && form.subcontractor_ids.length === 0) {
      toast.error("Sélectionnez au moins un salarié ou sous-traitant");
      return;
    }
    try {
      setSaving(true);
      const { data } = await api.createOrderWorkSessions(order.id, form);
      toast.success(`${data.created} créneau(x) généré(s)`);
      // Refresh workers list
      const fresh = await api.getOrderWorkers(order.id);
      const wks = fresh.data?.workers || [];
      setExisting({
        employees: wks
          .filter((w) => w.kind === "employee")
          .map((w) => ({ employee_id: w.id, name: w.name, days_worked: w.total_days, total_hours: w.total_hours })),
        subcontractors: wks
          .filter((w) => w.kind === "subcontractor")
          .map((w) => ({ subcontractor_id: w.id, name: w.name, days_worked: w.total_days, total_hours: w.total_hours })),
      });
      setForm({ ...form, start_date: "", end_date: "", employee_ids: [], subcontractor_ids: [] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'ajout");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="sm:max-w-[720px] max-h-[85vh] overflow-y-auto" data-testid="work-sessions-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><HardHat className="h-5 w-5 text-indigo-600" />Jours et heures travaillées</DialogTitle>
          <DialogDescription>
            {order?.reference} — {order?.client_name}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : (
          <div className="space-y-5">
            {/* Existing workers */}
            {(existing.employees.length > 0 || existing.subcontractors.length > 0) && (
              <div className="rounded-lg border bg-slate-50 p-3 space-y-2" data-testid="existing-workers-block">
                <div className="text-xs font-medium text-slate-700 flex items-center gap-1"><Clock className="h-3 w-3" />Déjà enregistrés sur ce chantier</div>
                <div className="flex flex-wrap gap-2">
                  {existing.employees.map((e) => (
                    <Badge key={e.employee_id} variant="outline" className="border-indigo-300 text-indigo-800">
                      <UserCog className="h-3 w-3 mr-1" />{e.name} · {e.days_worked} j · {e.total_hours} h
                    </Badge>
                  ))}
                  {existing.subcontractors.map((s) => (
                    <Badge key={s.subcontractor_id} variant="outline" className="border-amber-300 text-amber-800">
                      <Briefcase className="h-3 w-3 mr-1" />{s.name} · {s.days_worked} j · {s.total_hours} h
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Date range + slot */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs">Date de début</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="ws-start-date" />
              </div>
              <div>
                <Label className="text-xs">Date de fin</Label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="ws-end-date" />
              </div>
              <div>
                <Label className="text-xs">Créneau</Label>
                <Select value={form.slot} onValueChange={(v) => setForm({ ...form, slot: v })}>
                  <SelectTrigger data-testid="ws-slot"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="both">Journée complète</SelectItem>
                    <SelectItem value="morning">Matin uniquement</SelectItem>
                    <SelectItem value="afternoon">Après-midi uniquement</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end gap-2">
                <Checkbox
                  id="ws-weekends"
                  checked={form.include_weekends}
                  onCheckedChange={(v) => setForm({ ...form, include_weekends: !!v })}
                  data-testid="ws-weekends"
                />
                <Label htmlFor="ws-weekends" className="text-xs cursor-pointer pb-2">Inclure week-ends</Label>
              </div>
            </div>

            {/* Employees list */}
            <div>
              <div className="text-xs font-medium text-slate-700 mb-1 flex items-center gap-1"><UserCog className="h-3 w-3" />Salariés</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 max-h-40 overflow-y-auto border rounded p-2">
                {employees.length === 0 ? (
                  <span className="text-xs text-muted-foreground col-span-full">Aucun salarié</span>
                ) : employees.map((e) => (
                  <label key={e.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 px-1 py-0.5 rounded">
                    <Checkbox
                      checked={form.employee_ids.includes(e.id)}
                      onCheckedChange={() => toggleEmployee(e.id)}
                      data-testid={`ws-emp-${e.id}`}
                    />
                    <span className="truncate">{e.first_name} {e.last_name}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Subcontractors list */}
            <div>
              <div className="text-xs font-medium text-slate-700 mb-1 flex items-center gap-1"><Briefcase className="h-3 w-3" />Sous-traitants</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 max-h-40 overflow-y-auto border rounded p-2">
                {subcontractors.length === 0 ? (
                  <span className="text-xs text-muted-foreground col-span-full">Aucun sous-traitant</span>
                ) : subcontractors.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 px-1 py-0.5 rounded">
                    <Checkbox
                      checked={form.subcontractor_ids.includes(s.id)}
                      onCheckedChange={() => toggleSubcontractor(s.id)}
                      data-testid={`ws-sub-${s.id}`}
                    />
                    <span className="truncate">{s.company_name || `${s.first_name || ""} ${s.last_name || ""}`.trim()}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Fermer</Button>
          <Button
            onClick={handleSubmit}
            disabled={saving || loading}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="ws-save-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Users className="h-4 w-4 mr-1" />Générer les créneaux</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WorkSessionsDialog;
